import { useState, useEffect } from "react";
import { api } from "../../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Calendar, DollarSign, CheckCircle2, Trash2, Send, MessageSquarePlus, Clock } from "lucide-react";

const STATUS_OPTIONS = ["new", "contacted", "qualified", "won", "lost"];

export default function LeadDetail({ lead, open, onClose, onChanged, onDeleted }) {
  const [noteText, setNoteText] = useState("");
  const [estimate, setEstimate] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [savingEstimate, setSavingEstimate] = useState(false);

  useEffect(() => {
    setEstimate(lead?.estimated_recovery ? String(lead.estimated_recovery) : "");
    setNoteText("");
  }, [lead?.id]);

  if (!lead) return null;

  const updateStatus = async (status) => {
    try {
      const { data } = await api.patch(`/admin/leads/${lead.id}`, { status });
      onChanged?.(data);
      toast.success(`Status → ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const markContacted = async () => {
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/mark-contacted`);
      onChanged?.(data);
      toast.success("Marked as contacted");
    } catch {
      toast.error("Update failed");
    }
  };

  const saveEstimate = async () => {
    setSavingEstimate(true);
    try {
      const val = estimate === "" ? null : Number(estimate);
      const { data } = await api.patch(`/admin/leads/${lead.id}/estimate`, { estimated_recovery: val });
      onChanged?.(data);
      toast.success("Estimate saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSavingEstimate(false);
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/notes`, { text: noteText.trim() });
      onChanged?.(data);
      setNoteText("");
      toast.success("Note added");
    } catch {
      toast.error("Failed to add note");
    } finally {
      setSavingNote(false);
    }
  };

  const deleteNote = async (noteId) => {
    try {
      const { data } = await api.delete(`/admin/leads/${lead.id}/notes/${noteId}`);
      onChanged?.(data);
    } catch {
      toast.error("Delete failed");
    }
  };

  const deleteLead = async () => {
    if (!window.confirm("Delete this lead permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/leads/${lead.id}`);
      onDeleted?.(lead.id);
      toast.success("Lead deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const emailSubject = encodeURIComponent(`Following up on your foreclosure surplus inquiry`);
  const emailBody = encodeURIComponent(`Hello ${lead.name.split(" ")[0]},\n\nThank you for reaching out to American Asset Recovery Agency. I'd like to follow up regarding ${lead.property_address ? `the property at ${lead.property_address}` : "your inquiry"} and discuss potential surplus funds recovery.\n\nWhen is a good time for a brief call?\n\nBest regards,\nAmerican Asset Recovery Agency\n1-833-535-9505`);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto bg-[#F7F5F0]" data-testid="lead-detail-sheet">
        <SheetHeader>
          <SheetTitle className="font-serif-display text-2xl text-[#0F172A]">{lead.name}</SheetTitle>
          <SheetDescription className="text-xs text-[#64748B]">
            Submitted {new Date(lead.created_at).toLocaleString()}{lead.utm_source ? ` · via ${lead.utm_source}` : ""}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 mt-6">
          {/* Quick actions */}
          <div className="flex flex-wrap gap-2">
            <a href={`tel:${lead.phone}`} className="inline-flex items-center text-sm bg-white border border-[#E2DDD3] rounded-full px-4 py-2 hover:border-[#FF008F]" data-testid="action-call">
              <Phone className="w-4 h-4 mr-2 text-[#1E3A8A]" /> Call {lead.phone}
            </a>
            <a href={`mailto:${lead.email}?subject=${emailSubject}&body=${emailBody}`} className="inline-flex items-center text-sm bg-white border border-[#E2DDD3] rounded-full px-4 py-2 hover:border-[#FF008F]" data-testid="action-email">
              <Send className="w-4 h-4 mr-2 text-[#1E3A8A]" /> Email lead
            </a>
            <Button onClick={markContacted} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white rounded-full" data-testid="action-mark-contacted">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Mark contacted
            </Button>
          </div>
          {lead.contacted_at && (
            <p className="text-xs text-[#475569] flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Last contacted: {new Date(lead.contacted_at).toLocaleString()}
            </p>
          )}

          {/* Status */}
          <div>
            <p className="text-xs uppercase tracking-wider text-[#64748B] mb-2">Status</p>
            <Select value={lead.status} onValueChange={updateStatus}>
              <SelectTrigger className="bg-white border-[#E2DDD3]" data-testid={`status-select-${lead.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Contact details */}
          <div className="bg-white rounded-2xl border border-[#E2DDD3] p-4 space-y-2 text-sm">
            <Row icon={<Mail className="w-4 h-4 text-[#1E3A8A]" />} label="Email" value={<a href={`mailto:${lead.email}`} className="hover:text-[#FF008F]">{lead.email}</a>} />
            <Row icon={<Phone className="w-4 h-4 text-[#1E3A8A]" />} label="Phone" value={<a href={`tel:${lead.phone}`} className="hover:text-[#FF008F]">{lead.phone}</a>} />
            {lead.property_address && <Row icon={<MapPin className="w-4 h-4 text-[#1E3A8A]" />} label="Property" value={`${lead.property_address}${lead.county ? `, ${lead.county}` : ""}${lead.state ? `, ${lead.state}` : ""}`} />}
            {lead.foreclosure_date && <Row icon={<Calendar className="w-4 h-4 text-[#1E3A8A]" />} label="Foreclosure" value={lead.foreclosure_date} />}
            {lead.message && (
              <div className="pt-2 border-t border-[#E2DDD3]">
                <p className="text-xs uppercase tracking-wider text-[#64748B]">Message</p>
                <p className="text-sm text-[#0F172A] mt-1 whitespace-pre-wrap">{lead.message}</p>
              </div>
            )}
          </div>

          {/* Attribution */}
          {(lead.utm_source || lead.utm_campaign || lead.referrer) && (
            <div className="bg-white rounded-2xl border border-[#E2DDD3] p-4 text-sm">
              <p className="text-xs uppercase tracking-wider text-[#64748B] mb-2">Attribution</p>
              <div className="grid grid-cols-2 gap-2 text-[13px]">
                {lead.utm_source && <Field label="Source" value={lead.utm_source} />}
                {lead.utm_medium && <Field label="Medium" value={lead.utm_medium} />}
                {lead.utm_campaign && <Field label="Campaign" value={lead.utm_campaign} />}
                {lead.referrer && <Field label="Referrer" value={lead.referrer} />}
              </div>
            </div>
          )}

          {/* Estimate */}
          <div className="bg-white rounded-2xl border border-[#E2DDD3] p-4">
            <p className="text-xs uppercase tracking-wider text-[#64748B] mb-2 flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5" /> Estimated recovery (USD)
            </p>
            <div className="flex gap-2">
              <Input
                type="number"
                inputMode="decimal"
                value={estimate}
                onChange={(e) => setEstimate(e.target.value)}
                placeholder="e.g. 25000"
                className="bg-[#F7F5F0] border-[#E2DDD3]"
                data-testid="estimate-input"
              />
              <Button onClick={saveEstimate} disabled={savingEstimate} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="save-estimate">
                Save
              </Button>
            </div>
            {lead.estimated_recovery > 0 && (
              <p className="mt-2 text-sm text-green-700 font-medium">${Number(lead.estimated_recovery).toLocaleString()} in pipeline</p>
            )}
          </div>

          {/* Notes */}
          <div className="bg-white rounded-2xl border border-[#E2DDD3] p-4">
            <p className="text-xs uppercase tracking-wider text-[#64748B] mb-3 flex items-center gap-1.5">
              <MessageSquarePlus className="w-3.5 h-3.5" /> Internal notes ({lead.notes?.length || 0})
            </p>
            <div className="space-y-3 mb-3">
              {(lead.notes || []).slice().reverse().map((n) => (
                <div key={n.id} className="bg-[#F7F5F0] rounded-lg p-3 text-sm" data-testid={`note-${n.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-xs text-[#64748B]">{n.author_name || n.author_email} · {new Date(n.created_at).toLocaleString()}</p>
                    <button onClick={() => deleteNote(n.id)} className="text-[#94A3B8] hover:text-red-600 text-xs">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <p className="text-[#0F172A] whitespace-pre-wrap">{n.text}</p>
                </div>
              ))}
            </div>
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a private note (e.g. 'Left voicemail 12/15, will call back Tuesday')"
              className="bg-[#F7F5F0] border-[#E2DDD3] min-h-20"
              data-testid="note-input"
            />
            <Button onClick={addNote} disabled={savingNote || !noteText.trim()} className="mt-2 bg-[#FF008F] hover:bg-[#D60079] text-white" data-testid="add-note-button">
              Add note
            </Button>
          </div>

          {/* Danger zone */}
          <div className="pt-2 border-t border-[#E2DDD3]">
            <Button onClick={deleteLead} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="delete-lead-button">
              <Trash2 className="w-4 h-4 mr-2" /> Delete this lead
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Row({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-[#64748B]">{label}</p>
        <p className="text-sm text-[#0F172A] truncate">{value}</p>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-[#64748B]">{label}</p>
      <p className="text-sm text-[#0F172A] truncate">{value}</p>
    </div>
  );
}
